import React, { useCallback } from "react"
import { type FieldError, useFormContext, type RegisterOptions } from "react-hook-form"
import { useTranslation } from "react-i18next"
import Checkbox, { type CheckboxProps } from "./checkbox"
import { type FormInputs } from "./form_input"
import { dig } from "@/common/util/misc"

interface FormCheckboxProps extends CheckboxProps {
  name: string
  rules?: RegisterOptions
  errorInterpolation?: object
}
const ForwardableFormCheckbox: React.ForwardRefRenderFunction<HTMLInputElement, FormCheckboxProps> = (
  { rules, name, errorInterpolation, ...props },
  ref
) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<FormInputs>()
  const error = dig(errors, name) as FieldError | undefined
  const registerFields = register(name, rules)

  const { t } = useTranslation()
  const tOptions: Record<string, string> = { ...errorInterpolation }
  const errorMessage = error && (error.message ? t(error.message, tOptions) : t("misc.required_field"))

  const onChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      await registerFields.onChange(e)

      if (props.onChange) props.onChange(e)
    },
    [props, registerFields]
  )

  return (
    <Checkbox
      {...props}
      registerFields={registerFields}
      name={name}
      onChange={onChange}
      error={errorMessage}
      ref={ref}
    />
  )
}

const FormCheckbox = React.forwardRef(ForwardableFormCheckbox)

export default FormCheckbox
